import { useCallback, useEffect, useReducer, useState } from "react";
import type { AgentDto, Realm, StatsDto, TopicDto } from "@sikilesi/protocol";
import { useServices } from "../components/Services";
import { actionForEvent, feedReducer, initialFeed, type FeedState } from "../lib/feed-state";
import { SseClient, type SseStatus } from "../lib/sse";

export interface LiveFeed {
  realm: Realm;
  state: FeedState;
  agents: AgentDto[];
  stats: StatsDto | null;
  /** tam.sikile.si only: gundem findings that reached TAM ("TARİHE GEÇTİ"). */
  promoted: TopicDto[];
  status: SseStatus;
  error: string | null;
  loadingMore: boolean;
  loadMore: () => void;
  /** Apply a stamp result locally (the stream echoes it too; the reducer is idempotent). */
  applyStamp: (s: { topic_id: string; stamp_count: number; level: FeedState["topics"][number]["level"] }) => void;
}

/**
 * One realm's feed: first page + agents + stats over REST, then the realm's
 * SSE stream (history replay from Last-Event-ID 0 fills the terminal). A
 * `gap`, or every `poll` while the budget shield pauses live streaming,
 * reloads the REST lists; a poll also fetches the newest terminal lines
 * (GET /api/v1/activity) so CANLI GÖZLEM never sits empty. The tam edition also shows the TARİHE GEÇTİ strip,
 * refreshed with the stats every minute (those findings stay in gundem, so
 * the tarih stream does not carry them).
 */
export function useLiveFeed(realm: Realm): LiveFeed {
  const { api, streamUrl } = useServices();
  const [state, dispatch] = useReducer(feedReducer, realm, initialFeed);
  const [agents, setAgents] = useState<AgentDto[]>([]);
  const [stats, setStats] = useState<StatsDto | null>(null);
  const [promoted, setPromoted] = useState<TopicDto[]>([]);
  const [status, setStatus] = useState<SseStatus>("connecting");
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const refreshPromoted = useCallback(() => {
    if (realm !== "tarih") return;
    api.promoted().then(
      (page) => {
        setPromoted(page.items);
      },
      () => undefined,
    );
  }, [api, realm]);

  const reload = useCallback(
    async (replace: boolean) => {
      try {
        const [page, agentPage, s] = await Promise.all([api.feed(realm), api.agents(), api.stats(realm)]);
        dispatch({ type: "loaded", items: page.items, nextCursor: page.next_cursor, replace, page: "first" });
        setAgents(agentPage.items);
        setStats(s);
        setError(null);
      } catch {
        setError("Akış yüklenemedi. Yeniden deneniyor…");
      }
      refreshPromoted();
    },
    [api, realm, refreshPromoted],
  );

  useEffect(() => {
    void reload(false);
    const client = new SseClient({
      fetch: api.fetchImpl,
      url: streamUrl(`/api/v1/stream?realm=${realm}`),
      lastEventId: "0",
      onStatus: setStatus,
      onGap: () => {
        void reload(true);
      },
      onPoll: () => {
        // Budget polling: the stream carries nothing, so the terminal lines come over REST too.
        void reload(true);
        api.activity(realm).then(
          (items) => {
            dispatch({ type: "activityBatch", items });
          },
          () => undefined,
        );
      },
      onEvent: (msg) => {
        const action = actionForEvent(msg);
        if (action) dispatch(action);
      },
    });
    client.start();
    const timer = setInterval(() => {
      api.stats(realm).then(setStats, () => undefined);
      refreshPromoted();
    }, 60_000);
    return () => {
      client.stop();
      clearInterval(timer);
    };
  }, [api, realm, streamUrl, reload, refreshPromoted]);

  const loadMore = useCallback(() => {
    if (!state.nextCursor || loadingMore) return;
    setLoadingMore(true);
    api
      .feed(realm, state.nextCursor)
      .then((page) => {
        dispatch({ type: "loaded", items: page.items, nextCursor: page.next_cursor, replace: false, page: "next" });
      })
      .catch(() => {
        setError("Daha fazla bulgu yüklenemedi.");
      })
      .finally(() => {
        setLoadingMore(false);
      });
  }, [api, realm, state.nextCursor, loadingMore]);

  const applyStamp = useCallback<LiveFeed["applyStamp"]>((s) => {
    dispatch({ type: "stamped", stamp: { ...s, level_label: "" } });
  }, []);

  return { realm, state, agents, stats, promoted, status, error, loadingMore, loadMore, applyStamp };
}
